import { useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTokenClipboard } from "./useTokenClipboard";

export default function APITokenRow({ token, showAccountCol, gridCols, onEdit, onDelete, onDisable, onEnable, onHistory }) {
  const [tokenVisible, setTokenVisible] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const { copyToken } = useTokenClipboard();

  const workflowDisplay = token.assignedWorkflows?.length
    ? token.assignedWorkflows.map((wf) => `${wf.workflowGroup}/${wf.workflowName}`).join(", ")
    : "—";

  const openMenu = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const handleEdit = () => { closeMenu(); onEdit(token); };
  const handleDisable = () => { closeMenu(); onDisable(token); };
  const handleEnable = () => { closeMenu(); onEnable(token); };
  const handleDelete = () => { closeMenu(); onDelete(token); };
  const handleHistory = () => { closeMenu(); onHistory(token); };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: gridCols,
        px: 1.5,
        py: 1.1,
        color: "#4b5566",
        borderTop: "1px solid #e1e5ec",
        alignItems: "center",
        bgcolor: "white",
        "&:nth-of-type(even)": { bgcolor: "#f9fafc" },
        "&:hover": { bgcolor: "#f0f3f8" },
      }}
    >
      {/* Name */}
      <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: 1 }}>
        <Tooltip title={token.name} placement="top-start">
          <span>{token.name}</span>
        </Tooltip>
      </Box>

      {/* Account — Mode A only */}
      {showAccountCol && (
        <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: 1 }}>
          {token.account || "—"}
        </Box>
      )}

      {/* Assigned Workflows */}
      <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: 1 }}>
        <Tooltip title={workflowDisplay} placement="top-start">
          <span>{workflowDisplay}</span>
        </Tooltip>
      </Box>

      {/* Status */}
      <Box>
        <Typography
          component="span"
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            px: 0.9,
            py: 0.3,
            borderRadius: 1,
            bgcolor: token.status === "Active" ? "#e6f4ea" : "#fce8e6",
            color: token.status === "Active" ? "#1e6e38" : "#b0120a",
            border: `1px solid ${token.status === "Active" ? "#b7dfc3" : "#f4b8b4"}`,
          }}
        >
          {token.status}
        </Typography>
      </Box>

      {/* Token (masked) */}
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          sx={{
            fontFamily: "monospace",
            fontSize: "0.8rem",
            letterSpacing: 0.5,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 160,
          }}
        >
          {tokenVisible ? token.token : "••••••••••••••••••••"}
        </Typography>
        <Tooltip title={tokenVisible ? "Hide token" : "Show token"}>
          <IconButton
            size="small"
            onClick={() => setTokenVisible((v) => !v)}
            sx={{ color: "#6a7382", flexShrink: 0 }}
          >
            {tokenVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy token">
          <IconButton
            size="small"
            onClick={() => copyToken(token.token)}
            sx={{ color: "#6a7382", flexShrink: 0 }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Created By */}
      <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: 1 }}>
        {token.created_by || "—"}
      </Box>

      {/* Date */}
      <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: 1 }}>
        {token.date || "—"}
      </Box>

      {/* Actions — triple-dot menu */}
      <Box>
        <Tooltip title="Actions">
          <IconButton
            size="small"
            onClick={openMenu}
            sx={{
              color: "#6a7382",
              border: "1px solid #d2d8e2",
              borderRadius: 1,
              "&:hover": { bgcolor: "#eef1f6", borderColor: "#b8c3d4" },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              elevation: 3,
              sx: { minWidth: 170, borderRadius: 1.5, mt: 0.5 },
            },
          }}
        >
          <MenuItem onClick={handleEdit} dense>
            <ListItemIcon><EditIcon fontSize="small" sx={{ color: "#5b6473" }} /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>

          {token.status === "Active" ? (
            <MenuItem onClick={handleDisable} dense>
              <ListItemIcon><BlockIcon fontSize="small" sx={{ color: "#5b6473" }} /></ListItemIcon>
              <ListItemText>Disable</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem onClick={handleEnable} dense>
              <ListItemIcon><PlayArrowIcon fontSize="small" sx={{ color: "#5b6473" }} /></ListItemIcon>
              <ListItemText>Enable</ListItemText>
            </MenuItem>
          )}

          <MenuItem onClick={handleHistory} dense>
            <ListItemIcon><HistoryIcon fontSize="small" sx={{ color: "#5b6473" }} /></ListItemIcon>
            <ListItemText>View History</ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleDelete} dense sx={{ color: "error.main" }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
